import { PortalDialogProps } from "@app/services/modal.service";
import Modal from "../modal/modal.component";
import Button from "../button";
import { FormControl } from "../form-control";
import Input from "../input";
import { Form, Formik, FormikContextType } from "formik";
import { BankLink } from "@app/types";
import { createRef, useEffect } from "react";
import { linkAuthorBankValidationSchema } from "@app/validations";
import { CustomSelect } from "../select/custom-select";
import { VietnamBanks } from "@app/constants";
import ChevronDownArrow from "../select/chevron-down";

interface BankLinkModalProps {
  isEdit: boolean;

  bank: Partial<BankLink> | null;
}

function BankLinkModal({
  portalDialogRef,
  portalData,
}: PortalDialogProps<BankLinkModalProps>) {
  const formRef = createRef<
    FormikContextType<{
      bankName: string;
      bankNumber: string;
      bankOwner: string;
      identityNumber: string;
      issuedDate: string;
      issuedPlace: string;
      beneficiaryAddress: string;
    }>
  >();

  const onSubmit = (values: {
    bankName: string;
    bankNumber: string;
    bankOwner: string;
    identityNumber: string;
    issuedDate: string;
    issuedPlace: string;
    beneficiaryAddress: string;
  }) => {
    if (!formRef.current) return;

    const issuedDate = new Date(values.issuedDate).getTime();

    const bank = {
      bankName: values.bankName,
      bankNumber: values.bankNumber,
      bankOwner: values.bankOwner,
      identityNumber: values.identityNumber,
      issuedDate,
      issuedPlace: values.issuedPlace,
      beneficiaryAddress: values.beneficiaryAddress,
    } as BankLink;

    portalDialogRef.close({ bank });
  };

  useEffect(() => {
    if (portalData?.bank && formRef?.current) {
      formRef.current.setValues({
        bankName: portalData.bank.bankName || "",
        bankNumber: portalData.bank.bankNumber || "",
        bankOwner: portalData.bank.bankOwner || "",
        identityNumber: portalData.bank.identityNumber || "",
        issuedDate: portalData.bank.issuedDate
          ? new Date(portalData.bank.issuedDate).toISOString().split("T")[0]
          : "",
        issuedPlace: portalData.bank.issuedPlace || "",
        beneficiaryAddress: portalData.bank.beneficiaryAddress || "",
      });
    }
  }, [portalData?.bank]);

  return (
    <Modal onCancel={portalDialogRef.close}>
      <div className="w-[423px] py-7 px-6">
        <div className="text-black font-semibold text-xl">
          {portalData?.isEdit ? "Chỉnh sửa" : "Thông tin tài khoản ngân hàng"}
        </div>
        <Formik
          innerRef={formRef}
          initialValues={{
            bankName: "",
            bankNumber: "",
            bankOwner: "",
            identityNumber: "",
            issuedDate: "",
            issuedPlace: "",
            beneficiaryAddress: "",
          }}
          onSubmit={onSubmit}
          validationSchema={linkAuthorBankValidationSchema}
          validateOnChange
          validateOnBlur
        >
          <Form>
            <div className="mt-7">
              <div className="text-sm mb-2">Ngân hàng</div>
              <FormControl name="bankName">
                <CustomSelect
                  options={VietnamBanks.map((v) => ({ label: v, value: v }))}
                  className="bg-color !h-[52px] px-1 rounded-lg pr-4"
                  optionContainerClassName="mt-4 rounded-b-lg max-h-52 overflow-y-auto w-[375px]"
                  optionClassName="whitespace-nowrap px-3 py-3 border-b border-solid w-auto"
                  labelSelectedClassName="black-50 !mx-3"
                  placeholder="Chọn ngân hàng"
                  arrow={<ChevronDownArrow />}
                  hasBgColorHover
                />
              </FormControl>
            </div>
            <div className="mt-7">
              <div className="text-sm mb-2">Chủ thẻ</div>
              <FormControl name="bankOwner">
                <Input placeholder="Nhập tên chủ thẻ" />
              </FormControl>
            </div>
            <div className="mt-7">
              <div className="text-sm mb-2">Số tài khoản</div>
              <FormControl name="bankNumber">
                <Input placeholder="Nhập số tài khoản" />
              </FormControl>
            </div>
            <div className="mt-7">
              <div className="text-sm mb-2">Số căn cước công dân</div>
              <FormControl name="identityNumber">
                <Input placeholder="Nhập số căn cước công dân" />
              </FormControl>
            </div>
            <div className="mt-7">
              <div className="text-sm mb-2">Ngày cấp</div>
              <FormControl name="issuedDate">
                <Input type="date" placeholder="Chọn ngày cấp" />
              </FormControl>
            </div>
            <div className="mt-7">
              <div className="text-sm mb-2">Nơi cấp</div>
              <FormControl name="issuedPlace">
                <Input placeholder="Nhập nơi cấp" />
              </FormControl>
            </div>
            <div className="mt-7">
              <div className="text-sm mb-2">Địa chỉ người hưởng</div>
              <FormControl name="beneficiaryAddress">
                <Input placeholder="Nhập địa chỉ người hưởng" />
              </FormControl>
            </div>
            <div className="mt-11 flex justify-center gap-4">
              <Button
                type="submit"
                size="xs"
                label={portalData?.isEdit ? "Lưu" : "Liên kết"}
                width="100%"
                className="!h-[51px] !leading-[51px]"
                labelClassName="text-xl"
              />
            </div>
          </Form>
        </Formik>
      </div>
    </Modal>
  );
}

export default BankLinkModal;
